import { useState } from 'react'
import { Card, Table, Badge, Button, Modal, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'

const BudgetModifications = ({ budget }) => {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedModification, setSelectedModification] = useState(null)

  // Obtener modificaciones del presupuesto
  const { data: modifications, isLoading } = useQuery(
    ['budget-modifications', budget.id],
    async () => {
      const response = await api.get(`/financial/modificaciones-presupuesto/?presupuesto_id=${budget.id}`)
      return response.data.results || response.data || []
    },
    { enabled: !!budget.id }
  )

  // Mutación para aprobar/rechazar modificación
  const approveMutation = useMutation(
    async ({ modificationId, action }) => {
      return api.post(`/financial/modificaciones-presupuesto/${modificationId}/${action}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['budget-modifications', budget.id])
        queryClient.invalidateQueries(['budgets'])
        toast.success('Modificación procesada exitosamente')
      },
      onError: () => {
        toast.error('Error al procesar modificación')
      }
    }
  )

  const handleApprove = (modification) => {
    if (window.confirm('¿Estás seguro de aprobar esta modificación?')) {
      approveMutation.mutate({ modificationId: modification.id, action: 'aprobar' })
    }
  }

  const handleReject = (modification) => {
    if (window.confirm('¿Estás seguro de rechazar esta modificación?')) {
      approveMutation.mutate({ modificationId: modification.id, action: 'rechazar' })
    }
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { bg: 'warning', text: 'Pendiente' },
      'aprobada': { bg: 'success', text: 'Aprobada' },
      'rechazada': { bg: 'danger', text: 'Rechazada' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getTipoBadge = (tipo) => {
    const badges = {
      'incremento': { bg: 'success', text: 'Incremento' },
      'reduccion': { bg: 'danger', text: 'Reducción' },
      'reubicacion': { bg: 'info', text: 'Reubicación' }
    }
    const badge = badges[tipo] || { bg: 'secondary', text: tipo }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  if (isLoading) {
    return <div className="text-center py-5">Cargando...</div>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Modificaciones del Presupuesto</h5>
      </div>

      {modifications && modifications.length > 0 ? (
        <Card>
          <Card.Body>
            <Table responsive>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Línea</th>
                  <th>Monto Anterior</th>
                  <th>Monto Nuevo</th>
                  <th>Diferencia</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {modifications.map((modification) => {
                  const diferencia = parseFloat(modification.monto_nuevo || 0) - parseFloat(modification.monto_anterior || 0)
                  return (
                    <tr key={modification.id}>
                      <td>{formatDateOnlyEsMX(modification.fecha_solicitud, {}, '—')}</td>
                      <td>{getTipoBadge(modification.tipo)}</td>
                      <td>{modification.linea_presupuesto_code || modification.linea_presupuesto?.cuenta_contable?.code || 'N/A'}</td>
                      <td>
                        {formatCurrency(modification.monto_anterior || 0)}
                      </td>
                      <td>
                        {formatCurrency(modification.monto_nuevo || 0)}
                      </td>
                      <td>
                        <span className={diferencia >= 0 ? 'text-success' : 'text-danger'}>
                          {formatCurrency(diferencia)}
                        </span>
                      </td>
                      <td>{getEstadoBadge(modification.estado)}</td>
                      <td>
                        {modification.estado === 'pendiente' && (
                          <div className="btn-group" role="group">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleApprove(modification)}
                              disabled={approveMutation.isLoading}
                            >
                              Aprobar
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleReject(modification)}
                              disabled={approveMutation.isLoading}
                            >
                              Rechazar
                            </Button>
                          </div>
                        )}
                        {modification.justificacion && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => {
                              setSelectedModification(modification)
                              setShowForm(true)
                            }}
                          >
                            Ver Justificación
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          No hay modificaciones registradas para este presupuesto.
        </Alert>
      )}

      {/* Modal de justificación */}
      <Modal show={showForm} onHide={() => { setShowForm(false); setSelectedModification(null) }}>
        <Modal.Header closeButton>
          <Modal.Title>Justificación de Modificación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedModification && (
            <div>
              <p><strong>Justificación:</strong></p>
              <p>{selectedModification.justificacion || 'No hay justificación disponible'}</p>
              {selectedModification.aprobado_por_name && (
                <p className="mt-3">
                  <strong>Aprobado por:</strong> {selectedModification.aprobado_por_name}
                </p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowForm(false); setSelectedModification(null) }}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default BudgetModifications

